import { getResizedImage } from 'shared-helpers/common';
import { useDateNow, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import { Link, TimeIndicator, Tooltip } from 'components/common';
import { Icon } from 'components/common/icons';

import type { UserWithBasicData } from 'shared-types';

interface Props {
  autoFocus?: boolean;
  anonymous?: boolean;
  avatarStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  date?: string;
  enableProfileLink?: boolean;
  linkAs?: string;
  linkHref?: string;
  onClick?: () => void;
  showAvatar?: boolean;
  showEmail?: boolean;
  showEmailTooltip?: boolean;
  showNameOrUsername?: boolean;
  size?: 'tiny' | 'small' | 'medium' | 'large' | 'huge';
  style?: React.CSSProperties;
  subtitle?: React.ReactNode;
  subtitleStyle?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  user?: UserWithBasicData;
  vertical?: boolean;
}

// const backgroundColors = [
//   '#8d5a97',
//   '#4d9de0',
//   '#e15554',
//   '#e1bc29',
//   '#b4dc7f',
//   '#f78154',
//   '#baa898'
// ];

const backgroundColors = [
  '#b089b8',
  '#82bae9',
  '#ea8887',
  '#e9d069',
  '#cae6a5',
  '#f9a687',
  '#cec2b6'
];

const getBackgroundColor = (hash: number) => {
  return backgroundColors[hash % backgroundColors.length];
};

const getHash = (username: string) => {
  let hash = 0;
  const len = username.length;
  for (let i = 0; i < len; i++) {
    const chr = username.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash = hash >>> 0;
  }
  return hash;
};

const getInitials = (username: string) => {
  const parts = username.split(/[ \.]/);
  let initials = parts[0].charAt(0);
  if (parts.length > 1) initials += parts[1].charAt(0);
  return initials.toUpperCase();
};

const User = ({
  anonymous = false,
  avatarStyle,
  contentStyle,
  date,
  enableProfileLink,
  linkAs,
  linkHref,
  onClick,
  showAvatar = true,
  showEmail = true,
  showEmailTooltip = true,
  showNameOrUsername = true,
  size = 'medium',
  style,
  subtitle,
  subtitleStyle,
  titleStyle,
  user,
  vertical = false
}: Props) => {
  const { dateNow } = useDateNow();

  const {
    language,
    messages,
    serverConfig: { accountGuestUseName, userUseName },
    theme
  } = useReduxState();

  if (!user && !anonymous) return null;

  const initials = anonymous
    ? 'A'
    : ((!user.roles?.includes('guest') && userUseName) ||
        (user.roles?.includes('guest') && accountGuestUseName)) &&
      user.firstName &&
      user.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : getInitials(user.username || user.email.split('@')[0]);

  const backgroundColor = anonymous
    ? theme.lightGreyDark
    : getBackgroundColor(getHash(user.username || user.email));

  let iconFontSize = '1.5rem';
  let iconPadding = '7px 0';
  let iconSize = '30px';
  let subtitleFontSize = '1.2rem';
  let titleFontSize = '1.3rem';

  if (size === 'tiny') {
    iconFontSize = '1.1rem';
    iconPadding = '5px 0';
    iconSize = '20px';
    subtitleFontSize = '1.1rem';
    titleFontSize = '1.2rem';
  } else if (size === 'small') {
    iconFontSize = '1.3rem';
    iconPadding = '6px 0';
    iconSize = '26px';
    subtitleFontSize = '1.1rem';
    titleFontSize = '1.2rem';
  } else if (size === 'large') {
    iconFontSize = '1.6rem';
    iconPadding = '8px 0';
    iconSize = '34px';
    subtitleFontSize = '1.3rem';
    titleFontSize = '1.4rem';
  } else if (size === 'huge') {
    iconFontSize = '2.4rem';
    iconPadding = '20px 0';
    iconSize = '64px';
    subtitleFontSize = '1.6rem';
    titleFontSize = '1.8rem';
  }

  const accountExpired = Date.parse(user?.expirationDate as string) < dateNow;

  const emailIssues = user?.emailSendingIssue;

  const userIssuesTooltip = [
    emailIssues ? messages.usersEmailSendingIssue : '',
    accountExpired ? messages.usersAccountExpired : '',
    user?.locked ? messages.usersAccountLocked : ''
  ].filter((message) => message);

  const deactivatedAccount = accountExpired || user?.locked;

  return (
    <div
      className="user"
      style={{ ...style, opacity: deactivatedAccount ? 0.6 : 1 }}
    >
      {showAvatar ? (
        <div
          aria-hidden="true"
          className="avatar"
          style={{
            ...(user?.avatar
              ? {
                  backgroundImage: `url(${getResizedImage({
                    size: 480,
                    url: user.avatar
                  })})`
                }
              : { backgroundColor }),
            ...avatarStyle
          }}
        >
          {!user?.avatar ? initials : null}
        </div>
      ) : null}

      {showNameOrUsername ? (
        <div className="content" style={contentStyle}>
          {enableProfileLink &&
          !anonymous &&
          (!user.isAnonymous || (linkAs && linkHref)) &&
          (!user.roles?.includes('vuoadmin') || (linkAs && linkHref)) ? (
            <Link
              as={linkAs || `/users/${user.id}`}
              href={linkHref || '/users/[userId]'}
              linkColor="inherit"
            >
              <div
                className="title"
                style={titleStyle}
                {...(!anonymous &&
                !user.roles?.includes('guest') &&
                showEmailTooltip &&
                (date || !showEmail)
                  ? { 'aria-label': user.email, 'data-tooltip': user.email }
                  : {})}
              >
                {anonymous || user?.isAnonymous
                  ? `${messages.commonAnonymous}${user?.id || ''}`
                  : ((!user.roles?.includes('guest') && userUseName) ||
                      (user.roles?.includes('guest') && accountGuestUseName)) &&
                    user.firstName &&
                    user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user.roles?.includes('guest')
                  ? `${messages.usersGuest}${user.id}`
                  : user.username}
              </div>
            </Link>
          ) : (
            <div
              className={'title' + (onClick ? ' link' : '')}
              style={titleStyle}
              {...(onClick ? { onClick } : {})}
              {...(!anonymous &&
              !user.roles?.includes('guest') &&
              showEmailTooltip &&
              (date || !showEmail)
                ? { 'aria-label': user.email, 'data-tooltip': user.email }
                : {})}
            >
              {anonymous || user?.isAnonymous
                ? `${messages.commonAnonymous}${user?.id || ''}`
                : ((!user.roles?.includes('guest') && userUseName) ||
                    (user.roles?.includes('guest') && accountGuestUseName)) &&
                  user.firstName &&
                  user.lastName
                ? `${user.firstName} ${user.lastName}`
                : user.roles?.includes('guest')
                ? `${messages.usersGuest}${user.id}`
                : user.username}
            </div>
          )}

          {date ||
          subtitle ||
          (showEmail && !anonymous && !user?.isAnonymous) ||
          (user.roles?.includes('guest') &&
            accountGuestUseName &&
            user.firstName &&
            user.lastName) ? (
            <div className="subtitle" style={subtitleStyle}>
              {date ? (
                <span
                  aria-label={getFormattedDate({
                    date,
                    dateFormat: 'dd.MM.y HH:mm'
                  })}
                  data-tooltip={getFormattedDate({
                    date,
                    dateFormat: 'dd.MM.y HH:mm'
                  })}
                >
                  <TimeIndicator language={language} timeString={date} />
                </span>
              ) : subtitle ? (
                subtitle
              ) : user.roles?.includes('guest') &&
                accountGuestUseName &&
                user.firstName &&
                user.lastName ? (
                messages.usersGuestAccount
              ) : userIssuesTooltip.length ? (
                <Tooltip
                  content={userIssuesTooltip.join(', ')}
                  contentStyle={{
                    opacity: 1,
                    padding: '5px 8px',
                    wordBreak: 'break-word'
                  }}
                >
                  {user.email}

                  <div className="warnings">
                    {emailIssues ? <Icon icon="exclamation-triangle" /> : null}

                    {accountExpired ? (
                      <Icon
                        icon="exclamation-circle"
                        style={{ fontSize: '1.05rem' }}
                      />
                    ) : null}

                    {user.locked ? (
                      <Icon icon="lock" style={{ fontSize: '1.05rem' }} />
                    ) : null}
                  </div>
                </Tooltip>
              ) : (
                user.email
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <style jsx>{`
        .user {
          display: inline-block;
          line-height: 1;
          text-align: left;
          white-space: nowrap;
          word-break: break-all;
        }
        .avatar {
          background-color: ${theme.lightGrey};
          background-size: cover;
          border-radius: 40px;
          color: rgba(0, 0, 0, 0.5);
          display: inline-block;
          font-size: ${iconFontSize};
          font-weight: 700;
          padding: ${iconPadding};
          text-align: center;
          vertical-align: middle;
          height: ${iconSize};
          width: ${iconSize};
        }
        .content {
          display: ${vertical ? 'block' : 'inline-block'};
          font-size: ${vertical ? '1.5rem' : titleFontSize};
          font-weight: 600;
          margin-left: ${!vertical && showAvatar ? '7px' : '0'};
          margin-top: ${vertical && showAvatar ? '5px' : '0'};
          max-width: 200px;
          min-width: 80px;
          vertical-align: middle;
          white-space: normal;
        }
        .title {
          color: ${anonymous ? theme.textColorLighter : theme.textColorLight};
          font-size: ${vertical ? '1.8rem' : titleFontSize};
          font-weight: 700;
          margin-top: ${vertical ? '20px' : '0'};
        }
        .link {
          cursor: pointer;
          display: inline-block;
          text-decoration: none;
        }
        .link:focus,
        .link:hover {
          text-decoration: underline;
        }
        .subtitle {
          color: ${theme.textColorLighter};
          font-size: ${vertical ? '1.3rem' : subtitleFontSize};
          font-weight: 600;
          margin-top: ${vertical ? '5px' : '3px'};
        }
        .warnings {
          display: inline-flex;
          gap: 3px;
          font-size: 1rem;
          margin-left: 5px;
        }
      `}</style>
    </div>
  );
};

export default User;
